import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api'; // Importa o serviço de API configurado

// Estilos personalizados para o editor Quill
const quillStyles = `
  .ql-container {
    border: 1px solid #d1d5db;
    border-radius: 8px 0 0 8px;
    font-size: 0.875rem;
    height: 50px !important;
    overflow: hidden;
    transition: border-color 0.2s ease;
  }

  .ql-container:focus-within {
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
  }

  .ql-editor {
    padding: 12px;
    height: 50px !important;
    max-height: 50px !important;
    overflow-y: hidden;
    line-height: 1.4;
    word-break: break-word;
    overflow-wrap: break-word;
    white-space: pre-wrap; /* Preserva espaços e quebra linhas automaticamente */
  }

  .ql-toolbar {
    border: none;
    border-bottom: 1px solid #e5e7eb;
    border-radius: 8px 8px 0 0;
    display: none;
  }

  .ql-editor:focus {
    outline: none;
  }

  /* Estilos para exibição de mensagens */
  .message-text {
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  /* Estilos para barra de rolagem roxa */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #8b5cf6 #e9d5ff; /* Cor roxa do programa */
  }

  .custom-scrollbar::-webkit-scrollbar {
    width: 12px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f3e8ff; /* Fundo roxo claro */
    border-radius: 6px;
  }

  .custom-scrollbar::-webkit-scrollbar-track:hover {
    background: #e9d5ff;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: #8b5cf6; /* Cor roxa principal */
    border-radius: 6px;
    border: 2px solid #e9d5ff; /* Borda sutil */
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: #7c3aed; /* Tom mais escuro no hover */
  }

  /* Estilos específicos para o container de mensagens */
  .mensagens-container {
    scrollbar-width: thin;
    scrollbar-color: #8b5cf6 #e9d5ff;
  }

  /* Estilos para mobile para garantir que o layout funcione corretamente com o teclado virtual */
  @media (max-width: 768px) {
    .chat-container {
      height: 100vh;
      display: flex;
      flex-direction: column;
    }

    .mensagens-container {
      flex: 1;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch; /* Para rolagem suave no iOS */
    }
  }

  .mensagens-container::-webkit-scrollbar {
    width: 12px;
  }

  .mensagens-container::-webkit-scrollbar-track {
    background: #f3e8ff;
    border-radius: 6px;
  }

  .mensagens-container::-webkit-scrollbar-thumb {
    background-color: #8b5cf6;
    border-radius: 6px;
    border: 2px solid #e9d5ff;
  }

  .mensagens-container::-webkit-scrollbar-thumb:hover {
    background-color: #7c3aed;
  }

  /* Estilos para mobile para melhorar a experiência de digitação */
  @media (max-width: 768px) {
    #message-editor-container {
      -webkit-user-select: text;
      -moz-user-select: text;
      -ms-user-select: text;
      user-select: text;
      -webkit-touch-callout: default;
      -webkit-tap-highlight-color: transparent;
      outline: none;
    }
  }
`;

// Componente para exibir uma notícia no estilo Twitter

const NoticiaItem = ({ noticia, token, isAdmin, onDeleteNoticia }) => {
    const [liked, setLiked] = useState(false);
    const [likesCount, setLikesCount] = useState(noticia.curtidas?.length || 0);
    const [comments, setComments] = useState(noticia.comentarios || []);
    const [newComment, setNewComment] = useState('');
    const [showComments, setShowComments] = useState(false);

    // Função para curtir uma notícia
    const handleLike = async () => {
        try {
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            if (liked) {
                // Remover curtida
                await api.delete(`/api/conecta/noticias/${noticia._id}/like`, config);
                setLikesCount(prev => prev - 1);
            } else {
                // Adicionar curtida
                await api.post(`/api/conecta/noticias/${noticia._id}/like`, {}, config);
                setLikesCount(prev => prev + 1);
            }
            
            setLiked(!liked);
        } catch (error) {
            console.error('Erro ao curtir notícia:', error);
        }
    };

    // Função para adicionar comentário
    const handleAddComment = async () => {
        if (!newComment.trim()) return;

        try {
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const response = await api.post(`/api/conecta/noticias/${noticia._id}/comment`, {
                text: newComment
            }, config);

            setComments([...comments, response.data.comment]);
            setNewComment('');
        } catch (error) {
            console.error('Erro ao adicionar comentário:', error);
        }
    };

    // Função para deletar notícia (apenas para administradores)
    const handleDeleteNoticia = async () => {
        if (!isAdmin) return;
        
        if (window.confirm('Tem certeza que deseja remover esta publicação?')) {
            try {
                const config = {
                    headers: { Authorization: `Bearer ${token}` }
                };
                
                await api.delete(`/api/conecta/noticias/${noticia._id}`, config);
                // Chamar função para atualizar a lista de notícias
                if (onDeleteNoticia) {
                    onDeleteNoticia(noticia._id);
                }
            } catch (error) {
                console.error('Erro ao deletar notícia:', error);
                alert('Erro ao remover publicação. Tente novamente.');
            }
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 hover:shadow-md transition-shadow">
            {/*
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-violet-200 flex items-center justify-center mr-3 flex-shrink-0">
                        {noticia.autorInfo?.profileImageUrl ? (
                            <img 
                                src={noticia.autorInfo.profileImageUrl} 
                                alt={noticia.autorInfo.nome} 
                                className="w-full h-full rounded-full object-cover"
                            />
                        ) : (
                            <span className="font-semibold text-violet-800">
                                {noticia.autorInfo?.nome?.charAt(0) || 'V'}
                            </span>
                        )}
                    </div>
                    <div>
                        <div className="flex items-center">
                            <h3 className="font-bold text-gray-900">{noticia.autorInfo?.nome || 'Vestibula'}</h3>
                            {noticia.autorInfo?.isVerified && (
                                <svg className="w-4 h-4 text-purple-500 ml-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            )}
                        </div>
                        <p className="text-xs text-gray-500">
                            {new Date(noticia.createdAt).toLocaleDateString('pt-BR')}
                        </p>
                    </div>
                </div>
                {isAdmin && (
                    <button 
                        onClick={handleDeleteNoticia}
                        className="text-red-500 hover:text-red-700"
                        title="Remover publicação"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                    </button>
                )}
            </div>

            {/*
            <div className="mb-3">
                <h2 className="font-bold text-gray-900 mb-2">{noticia.title}</h2>
                <p className="text-gray-800 text-sm mb-3">{noticia.content}</p>
                {noticia.source && (
                    <p className="text-xs text-gray-500">Fonte: {noticia.source}</p>
                )}
            </div>
            }

            {/* Imagem da notícia - formato mais quadrado */}
            {noticia.imageUrl && (
                <div className="mb-3 rounded-lg overflow-hidden">
                    <img 
                        src={noticia.imageUrl} 
                        alt={noticia.title} 
                        className="w-full aspect-square object-cover"
                    />
                </div>
            )}

            {/* Interações */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="flex items-center space-x-4">
                    <button 
                        onClick={handleLike}
                        className={`flex items-center ${liked ? 'text-violet-600' : 'text-gray-500'} hover:text-violet-600`}
                    >
                        <svg className="w-5 h-5 mr-1" fill={liked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                        </svg>
                        <span className="text-sm">{likesCount}</span>
                    </button>
                    
                    <button 
                        onClick={() => setShowComments(!showComments)}
                        className="flex items-center text-gray-500 hover:text-violet-600"
                    >
                        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                        </svg>
                        <span className="text-sm">{comments.length}</span>
                    </button>
                </div>
            </div>

            {/* Seção de comentários */}
            {showComments && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                    {/* Lista de comentários */}
                    <div className="max-h-40 overflow-y-auto mb-3 space-y-2 custom-scrollbar">
                        {comments.map(comment => (
                            <div key={comment._id} className="flex items-start">
                                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-2 flex-shrink-0">
                                    <span className="text-xs font-semibold text-gray-700">
                                        {comment.autor?.nome?.charAt(0) || 'U'}
                                    </span>
                                </div>
                                <div className="bg-gray-100 rounded-lg p-2 flex-1">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-semibold text-xs text-gray-800">
                                            {comment.autor?.nome || 'Usuário'}
                                        </h4>
                                        <span className="text-xs text-gray-500">
                                            {new Date(comment.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-700 mt-1">{comment.texto}</p>
                                </div>
                            </div>
                        ))}
                        
                        {comments.length === 0 && (
                            <p className="text-gray-500 text-center py-2 text-sm">Nenhum comentário ainda. Seja o primeiro!</p>
                        )}
                    </div>
                    
                    {/* Formulário para novo comentário */}
                    <div className="flex">
                        <input
                            type="text"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Escreva um comentário..."
                            className="flex-1 p-2 text-sm border border-gray-300 rounded-l-lg focus:outline-none focus:ring-1 focus:ring-violet-500"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                        />
                        <button
                            onClick={handleAddComment}
                            disabled={!newComment.trim()}
                            className="bg-violet-600 text-white px-3 py-2 rounded-r-lg text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Enviar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};



// Componente para exibir um grupo (estilo WhatsApp)
const GrupoItem = ({ grupo, token, onJoinGroup, onLeaveGroup, onViewMessages, viewingMessages, onDeleteGroup }) => {
    const [loading, setLoading] = useState(false);

    // Função para lidar com entrada no grupo
    const handleJoinGroup = async () => {
        if (loading) return;
        
        setLoading(true);
        try {
            await onJoinGroup(grupo._id);
        } catch (error) {
            console.error('Erro ao entrar no grupo:', error);
        } finally {
            setLoading(false);
        }
    };

    // Função para lidar com saída do grupo
    const handleLeaveGroup = async () => {
        if (loading) return;
        
        setLoading(true);
        try {
            await onLeaveGroup(grupo._id);
        } catch (error) {
            console.error('Erro ao sair do grupo:', error);
        } finally {
            setLoading(false);
        }
    };

    // Função para visualizar mensagens
    const handleViewMessages = () => {
        onViewMessages(grupo._id);
    };

    // Função para deletar grupo (apenas para administradores)
    const handleDeleteGroup = async () => {
        if (!window.confirm('Tem certeza que deseja remover este grupo? Esta ação não pode ser desfeita.')) return;
        
        try {
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };
            
            await api.delete(`/api/conecta/grupos/${grupo._id}`, config);
            
            // Chamar função para atualizar a lista de grupos
            if (onDeleteGroup) {
                onDeleteGroup(grupo._id);
            }
        } catch (error) {
            console.error('Erro ao deletar grupo:', error);
            alert('Erro ao remover grupo. Tente novamente.');
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center p-3">
                {/* Avatar do grupo */}
                <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="font-semibold text-violet-600">{grupo.nome?.charAt(0) || 'G'}</span>
                </div>
                
                {/* Informações do grupo */}
                <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-gray-900 truncate">{grupo.nome}</h3>
                        <span className="text-xs text-gray-500">{grupo.membros?.length || 0} membros</span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{grupo.descricao}</p>
                </div>
                
                {/* Ações */}
                <div className="ml-2 flex space-x-2">
                    {grupo.usuarioMembro ? (
                        <button
                            onClick={handleViewMessages}
                            className="px-3 py-1 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 transition-colors"
                        >
                            Visualizar
                        </button>
                    ) : (
                        <button
                            onClick={handleJoinGroup}
                            disabled={loading}
                            className="px-3 py-1 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50"
                        >
                            {loading ? 'Entrando...' : 'Entrar'}
                        </button>
                    )}
                    
                    {/* Botão de deletar grupo (apenas para administradores) */}
                    {onDeleteGroup && (
                        <button 
                            onClick={handleDeleteGroup}
                            className="p-2 text-gray-500 hover:text-red-500 transition-colors"
                            title="Remover grupo"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const ConectaPage = ({ onVoltar, token, isAdmin: initialIsAdmin, user }) => {
    // Estados para as diferentes seções
    const [noticias, setNoticias] = useState([]);
    const [grupos, setGrupos] = useState([]);
    const [activeTab, setActiveTab] = useState('grupos'); // Changed from 'noticias' to 'grupos'
    const [subTab, setSubTab] = useState(null); // Initialize to null to show main cards
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');
    const [viewingMessagesGroupId, setViewingMessagesGroupId] = useState(null);
    const [messageError, setMessageError] = useState(null);
    const [joiningGroup, setJoiningGroup] = useState(null); // Track which group is being joined
    const [leavingGroup, setLeavingGroup] = useState(null); // Track which group is being left
    const [accessingGroup, setAccessingGroup] = useState(null); // Track which group is being accessed
    const [currentUser, setCurrentUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(initialIsAdmin || false);
    const [messageContent, setMessageContent] = useState(''); // New state for message content
    const [isSending, setIsSending] = useState(false); // New state for send button loading
    const [messageSent, setMessageSent] = useState(false); // Track if message has been sent
    const handleSendMessageRef = useRef(null);
    const messageContainerRef = useRef(null); // Reference for the message container
    const [showScrollButton, setShowScrollButton] = useState(false); // State for scroll to bottom button


    // Referência para o novo editor de mensagens
    const messageQuillRef = useRef(null);


    // Efeito para manipular o novo editor de mensagens
    useEffect(() => {
        if (messageQuillRef.current && viewingMessagesGroupId) {
            // Configurar o placeholder
            messageQuillRef.current.setAttribute('data-placeholder', 'Escreva sua mensagem...');

            // Função para atualizar o conteúdo quando o editor mudar
            const updateContent = () => {
                if (messageQuillRef.current) {
                    setMessageContent(messageQuillRef.current.innerHTML);
                }
            };

            // Adicionar listeners para quando o conteúdo mudar
            messageQuillRef.current.addEventListener('input', updateContent);
            messageQuillRef.current.addEventListener('focus', () => {
                // Garantir que o editor esteja habilitado
                if (messageQuillRef.current) {
                    messageQuillRef.current.setAttribute('contenteditable', 'true');
                }
            });

            // Limpar listeners quando o componente desmontar
            return () => {
                if (messageQuillRef.current) {
                    messageQuillRef.current.removeEventListener('input', updateContent);
                    messageQuillRef.current.removeEventListener('focus', updateContent);
                }
            };
        }
    }, [viewingMessagesGroupId]);

    // Efeito para garantir que o editor esteja habilitado e pronto quando o grupo de visualização de mensagens mudar
    useEffect(() => {
        if (viewingMessagesGroupId && messageQuillRef.current) {
            // Pequeno atraso para garantir que o editor esteja completamente pronto após montagem
            setTimeout(() => {
                if (messageQuillRef.current) {
                    messageQuillRef.current.setAttribute('contenteditable', 'true');
                    // Forçar foco no editor
                    messageQuillRef.current.focus();

                    // Colocar o cursor no final do conteúdo
                    const range = document.createRange();
                    const selection = window.getSelection();
                    range.selectNodeContents(messageQuillRef.current);
                    range.collapse(false); // Colapsar no final
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            }, 150); // Aumentar o tempo para garantir que o DOM esteja completamente montado
        }
    }, [viewingMessagesGroupId]);

    // Sincronizar o conteúdo do editor de posts com o estado
    
    // Variáveis para plano (não utilizadas mais, mas mantidas por compatibilidade)
    const planoMinimo = 'comum'; // Default value since permission checking is removed

    // Função para rolar para o final do container de mensagens
    const scrollToBottom = useCallback(() => {
        // Usar requestAnimationFrame para garantir que o DOM esteja completamente atualizado
        requestAnimationFrame(() => {
            const messagesContainers = document.querySelectorAll('.mensagens-container');
            if (messagesContainers.length > 0) {
                // Selecionar o container principal de mensagens (último na lista)
                const mainMessagesContainer = messagesContainers[messagesContainers.length - 1];
                if (mainMessagesContainer) {
                    // Rolar para o final para mostrar as mensagens mais recentes
                    mainMessagesContainer.scrollTop = mainMessagesContainer.scrollHeight;
                }
            }
        });
    }, []);

    // Efeito para rolar para o final quando mensagens são atualizadas
    useEffect(() => {
        if (viewingMessagesGroupId) {
            // Aguardar um pouco para garantir que o DOM foi atualizado
            setTimeout(() => {
                scrollToBottom();
            }, 150); // Aumentar o tempo para garantir renderização completa
        }
    }, [grupos, viewingMessagesGroupId, scrollToBottom]);

    // Efeito para rolar para o final sempre que as mensagens específicas do grupo mudarem
    useEffect(() => {
        if (viewingMessagesGroupId) {
            const currentGroup = grupos.find(g => g._id === viewingMessagesGroupId);
            if (currentGroup && currentGroup.messages) {
                // Usar setTimeout para garantir que o DOM foi atualizado com as novas mensagens
                setTimeout(() => {
                    scrollToBottom();
                }, 100);
            }
        }
    }, [grupos, viewingMessagesGroupId, scrollToBottom]);


    useEffect(() => {
        const fetchData = async () => {
            if (!token) return;

            try {
                setLoading(true);
                const config = {
                    headers: { Authorization: `Bearer ${token}` }
                };

                // Buscar informações do usuário para verificar se é administrador
                const userResponse = await api.get('/api/users/me', config);
                setIsAdmin(userResponse.data.user.isAdmin || false);
                setCurrentUser(userResponse.data.user); // Armazenar informações do usuário atual

                // Buscar grupos
                const gruposResponse = await api.get('/api/conecta/grupos', config);
                const gruposData = gruposResponse.data.grupos || [];

                // Check if user is in the "Alunos Vestibula" group, if not, they should be added automatically
                const alunosVestibulaGroup = gruposData.find(g => g.nome === 'Alunos Vestibula');

                if (alunosVestibulaGroup && !alunosVestibulaGroup.usuarioMembro) {
                    // Join the main group automatically
                    await handleJoinGroup(alunosVestibulaGroup._id);

                    // Refresh groups after joining
                    const updatedGruposResponse = await api.get('/api/conecta/grupos', config);
                    setGrupos(updatedGruposResponse.data.grupos || []);
                } else {
                    setGrupos(gruposData);
                }

                // Automatically go to the group chat after loading
                if (alunosVestibulaGroup) {
                    // Wait a moment for the UI to update, then go to the chat
                    setTimeout(() => {
                        handleViewMessages(alunosVestibulaGroup._id);
                    }, 100); // Reduzido o tempo para carregamento mais rápido
                } else {
                    // If the group wasn't in the initial data, find it after setting the state
                    setTimeout(() => {
                        const group = grupos.find(g => g.nome === 'Alunos Vestibula');
                        if (group) {
                            handleViewMessages(group._id);
                        }
                    }, 100); // Reduzido o tempo para carregamento mais rápido
                }
            } catch (err) {
                console.error('Erro ao buscar dados:', err);
                setError('Falha ao carregar os dados. Tente novamente.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [token, user]);


    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleJoinGroup = async (groupId) => {
        setJoiningGroup(groupId); // Set loading state for the specific group
        try {
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            await api.post(`/api/conecta/grupos/${groupId}/join`, {}, config);

            // Refresh only the specific group to ensure correct membership status, keeping messages from other groups
            const gruposResponse = await api.get('/api/conecta/grupos', config);
            const updatedGroups = gruposResponse.data.grupos || [];

            // Atualizar apenas o status de membrosia do grupo específico, mantendo as mensagens existentes
            setGrupos(prevGrupos => {
                return updatedGroups.map(updatedGroup => {
                    const existingGroup = prevGrupos.find(g => g._id === updatedGroup._id);
                    return {
                        ...updatedGroup,
                        messages: existingGroup ? existingGroup.messages : [] // Manter mensagens existentes
                    };
                });
            });

            setSuccessMessage('Você entrou no grupo com sucesso!');
            setErrorMessage('');
            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Erro ao entrar no grupo:', error);
            setErrorMessage('Erro ao entrar no grupo. Tente novamente.');
            setSuccessMessage('');
            // Clear error message after 3 seconds
            setTimeout(() => setErrorMessage(''), 3000);
        } finally {
            setJoiningGroup(null); // Reset loading state
        }
    };

    const handleLeaveGroup = async (groupId) => {
        // Check if this is the main "Alunos Vestibula" group
        const grupo = grupos.find(g => g._id === groupId);
        if (grupo && grupo.nome === 'Alunos Vestibula') {
            setErrorMessage('Você não pode sair do grupo principal Alunos Vestibula');
            setSuccessMessage('');
            // Clear error message after 3 seconds
            setTimeout(() => setErrorMessage(''), 3000);
            return;
        }

        setLeavingGroup(groupId); // Set loading state for the specific group
        try {
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            await api.post(`/api/conecta/grupos/${groupId}/leave`, {}, config);

            // Refresh only the specific group to ensure correct membership status, keeping messages from other groups
            const gruposResponse = await api.get('/api/conecta/grupos', config);
            const updatedGroups = gruposResponse.data.grupos || [];

            // Atualizar apenas o status de membrosia do grupo específico, mantendo as mensagens existentes
            setGrupos(prevGrupos => {
                return updatedGroups.map(updatedGroup => {
                    const existingGroup = prevGrupos.find(g => g._id === updatedGroup._id);
                    return {
                        ...updatedGroup,
                        messages: existingGroup ? existingGroup.messages : [] // Manter mensagens existentes
                    };
                });
            });

            // Fechar mensagens se estiverem abertas para este grupo
            if (viewingMessagesGroupId === groupId) {
                setViewingMessagesGroupId(null);
            }

            setSuccessMessage('Você saiu do grupo com sucesso!');
            setErrorMessage('');
            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Erro ao sair do grupo:', error);
            setErrorMessage('Erro ao sair do grupo. Tente novamente.');
            setSuccessMessage('');
            // Clear error message after 3 seconds
            setTimeout(() => setErrorMessage(''), 3000);
        } finally {
            setLeavingGroup(null); // Reset loading state
        }
    };

    // Função para deletar grupo (apenas para administradores)
    const handleDeleteGroup = async (groupId) => {
        try {
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            await api.delete(`/api/conecta/grupos/${groupId}`, config);

            // Refresh only the specific group to ensure correct state, keeping messages from other groups
            const gruposResponse = await api.get('/api/conecta/grupos', config);
            const updatedGroups = gruposResponse.data.grupos || [];

            // Atualizar a lista de grupos, mantendo as mensagens existentes
            setGrupos(prevGrupos => {
                return updatedGroups.map(updatedGroup => {
                    const existingGroup = prevGrupos.find(g => g._id === updatedGroup._id);
                    return {
                        ...updatedGroup,
                        messages: existingGroup ? existingGroup.messages : [] // Manter mensagens existentes
                    };
                });
            });

            // Fechar mensagens se estiverem abertas para este grupo
            if (viewingMessagesGroupId === groupId) {
                setViewingMessagesGroupId(null);
            }

            alert('Grupo removido com sucesso!');
        } catch (error) {
            console.error('Erro ao deletar grupo:', error);
            alert('Erro ao remover grupo. Tente novamente.');
        }
    };

    const handleViewMessages = async (groupId) => {
        if (!groupId) {
            setViewingMessagesGroupId(null);
            return;
        }

        setAccessingGroup(groupId); // Set loading state for accessing group
        try {
            // First, verify that the user is a member of the group
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            // Check if the user is a member of the group by fetching messages
            const response = await api.get(`/api/conecta/grupos/${groupId}/messages`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.data.success) {
                setErrorMessage('Você precisa ser membro deste grupo para visualizar as mensagens.');
                setSuccessMessage('');
                // Clear error message after 3 seconds
                setTimeout(() => setErrorMessage(''), 3000);
                return;
            }

            // Atualizar apenas o grupo específico com as novas mensagens, mantendo as mensagens dos outros grupos
            setGrupos(prevGrupos => prevGrupos.map(g => {
                if (g._id === groupId) {
                    return {
                        ...g,
                        messages: response.data.messages || [],
                        usuarioMembro: true // Confirmar que o usuário é membro
                    };
                }
                return g;
            }));

            setViewingMessagesGroupId(groupId);

            // Garantir que o editor esteja habilitado quando o usuário visualiza mensagens
            setTimeout(() => {
                if (messageQuillRef.current) {
                    messageQuillRef.current.setAttribute('contenteditable', 'true');
                }

                // Usar a função scrollToBottom para garantir que o scroll vá para o final
                scrollToBottom();
            }, 100);
        } catch (error) {
            console.error('Erro ao buscar mensagens do grupo:', error);

            // Check if the error is a 403 (Forbidden) and inform the user appropriately
            if (error.response && error.response.status === 403) {
                setErrorMessage('Acesso negado: Você precisa ser membro deste grupo para visualizar as mensagens.');
                setSuccessMessage('');
                // Clear error message after 3 seconds
                setTimeout(() => setErrorMessage(''), 3000);
                // Don't set viewingMessagesGroupId if user doesn't have access
                return;
            }

            // For other errors, show a generic error message
            setErrorMessage('Erro ao carregar mensagens. Tente novamente mais tarde.');
            setSuccessMessage('');
            // Clear error message after 3 seconds
            setTimeout(() => setErrorMessage(''), 3000);
        } finally {
            setAccessingGroup(null); // Reset loading state
        }
    };

    // Definir handleSendMessage e armazenar na ref para evitar problemas de referência circular
    const handleSendMessage = useCallback(async (groupId, messageText) => {
        // Prevenir múltiplos envios simultâneos
        if (isSending) return;

        setIsSending(true);
        // Limpar mensagens de erro anteriores
        setMessageError(null);

        // Use Quill content if available, otherwise use the text parameter
        let contentToSend = messageContent && messageContent.trim() ? messageContent : messageText;

        // Verificar se há conteúdo para enviar
        if (!contentToSend) {
            setMessageError('Por favor, digite uma mensagem antes de enviar.');
            setIsSending(false);
            return;
        }

        // Remover tags HTML para contar apenas texto e verificar se há conteúdo real
        const textContent = contentToSend.replace(/<[^>]*>/g, '').trim();

        if (!textContent) {
            setMessageError('Por favor, digite uma mensagem antes de enviar.');
            setIsSending(false);
            return;
        }

        // Verificar limite de caracteres (200)
        if (textContent.length > 200) {
            setMessageError('A mensagem não pode exceder 200 caracteres.');
            setIsSending(false);
            return;
        }

        // Declare tempMessageId in the function scope so it's accessible in both try and catch blocks
        let tempMessageId = null;

        try {
            // First, verify that the user is still a member of the group
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            // Verificar se o usuário ainda é membro do grupo sem atualizar todos os grupos
            const grupoResponse = await api.get(`/api/conecta/grupos/${groupId}/messages`, config);
            if (!grupoResponse.data.success) {
                setMessageError('Erro ao verificar permissão para enviar mensagem.');
                setIsSending(false);
                return;
            }

            // Mostrar mensagem temporária imediatamente para melhorar experiência do usuário
            tempMessageId = `temp-${Date.now()}`;
            const tempMessage = {
                _id: tempMessageId,
                texto: textContent,
                autor: currentUser,
                createdAt: new Date().toISOString(),
                isPending: true // Indica que é uma mensagem temporária
            };

            // Adicionar mensagem temporária imediatamente na interface
            setGrupos(prevGrupos => prevGrupos.map(g => {
                if (g._id === groupId) {
                    const updatedGroup = {
                        ...g,
                        messages: [...(g.messages || []), tempMessage]
                    };

                    // Rolar para o final após adicionar a mensagem temporária
                    setTimeout(() => {
                        scrollToBottom();
                    }, 50);

                    return updatedGroup;
                }
                return g;
            }));

            const response = await api.post(`/api/conecta/grupos/${groupId}/message`, {
                text: textContent // Enviar apenas o texto puro, não o HTML
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Verificar se a resposta contém os dados esperados
            if (!response.data || !response.data.message) {
                throw new Error('Resposta inválida do servidor');
            }

            // Atualizar o grupo com a nova mensagem real (substituir a temporária)
            setGrupos(prevGrupos => prevGrupos.map(g => {
                if (g._id === groupId) {
                    // Remover mensagem temporária e adicionar mensagem real
                    const filteredMessages = (g.messages || []).filter(msg => msg._id !== tempMessageId);

                    // Criar o objeto da nova mensagem com informações do servidor ou fallback
                    const newMessage = {
                        _id: response.data.message._id,
                        texto: response.data.message.texto || response.data.message.text || textContent,
                        autor: response.data.message.autor || currentUser,
                        createdAt: response.data.message.createdAt || new Date().toISOString()
                    };

                    const updatedGroup = {
                        ...g,
                        messages: [...filteredMessages, newMessage]
                    };

                    // Rolar para o final após atualizar as mensagens
                    setTimeout(() => {
                        scrollToBottom();
                    }, 50);

                    return updatedGroup;
                }
                return g;
            }));

            // Limpar o conteúdo do editor de mensagens e restaurar o foco
            if (messageQuillRef.current) {
                messageQuillRef.current.innerHTML = '';
                setMessageContent(''); // Limpar o estado também para garantir que o conteúdo seja limpo
                // Restaurar o foco no editor após limpar o conteúdo
                setTimeout(() => {
                    if (messageQuillRef.current) {
                        messageQuillRef.current.setAttribute('contenteditable', 'true');
                        messageQuillRef.current.focus();
                        // Colocar o caret no final do texto (que está vazio)
                        const range = document.createRange();
                        const selection = window.getSelection();
                        range.selectNodeContents(messageQuillRef.current);
                        range.collapse(false); // Colapsar no final
                        selection.removeAllRanges();
                        selection.addRange(range);
                    }
                }, 0);
            }

            // O estado viewingMessagesGroupId já está correto, não é necessário atualizar
        } catch (error) {
            console.error('Erro ao enviar mensagem:', error);

            // Remover mensagem temporária em caso de erro
            setGrupos(prevGrupos => prevGrupos.map(g => {
                if (g._id === groupId) {
                    const updatedGroup = {
                        ...g,
                        messages: (g.messages || []).filter(msg => msg._id !== tempMessageId) // Remover apenas a mensagem temporária específica
                    };

                    // Rolar para o final após atualizar as mensagens
                    setTimeout(() => {
                        scrollToBottom();
                    }, 50);

                    return updatedGroup;
                }
                return g;
            }));

            // Mostrar mensagem de erro mais específica com base no status da resposta
            if (error.response) {
                if (error.response.status === 403) {
                    setMessageError('Acesso negado: Você precisa ser membro deste grupo para enviar mensagens.');
                    // Close the message view if access is denied
                    if (viewingMessagesGroupId === groupId) {
                        setViewingMessagesGroupId(null);
                    }
                } else if (error.response.status === 400 && error.response.data && error.response.data.message) {
                    // Verificar se é a mensagem de limite de mensagens por hora
                    if (error.response.data.message.includes('Limite de 3 mensagens por hora atingido')) {
                        setMessageError('Limite de 10 mensagens por hora atingido. Aguarde um pouco antes de enviar outra mensagem.');
                    } else {
                        setMessageError(error.response.data.message);
                    }
                } else if (error.response.data && error.response.data.message) {
                    setMessageError(error.response.data.message);
                } else {
                    setMessageError('Erro ao enviar mensagem. Tente novamente.');
                }
            } else {
                setMessageError('Erro ao enviar mensagem. Tente novamente.');
            }

            // Restaurar o foco no editor mesmo em caso de erro
            if (messageQuillRef.current) {
                setTimeout(() => {
                    if (messageQuillRef.current) {
                        messageQuillRef.current.setAttribute('contenteditable', 'true');
                        messageQuillRef.current.focus();
                        // Colocar o caret no final do texto (que pode ter conteúdo após o erro)
                        const range = document.createRange();
                        const selection = window.getSelection();
                        range.selectNodeContents(messageQuillRef.current);
                        range.collapse(false); // Colapsar no final
                        selection.removeAllRanges();
                        selection.addRange(range);
                    }
                }, 0);
            }
        } finally {
            setIsSending(false);
        }
    }, [messageContent, currentUser, token, messageQuillRef, viewingMessagesGroupId, isSending, scrollToBottom]); // Add necessary dependencies

    // Armazenar a função na ref para que possa ser acessada por callbacks
    useEffect(() => {
        handleSendMessageRef.current = handleSendMessage;
    }, [handleSendMessage]);

    // Função para ajustar a altura do textarea
    const adjustTextareaHeight = () => {
        const textarea = messageQuillRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            const scrollHeight = textarea.scrollHeight;
            const maxHeight = 150;

            // Only show scrollbar if content exceeds max height
            textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
            textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
        }
    };

    // Handle Scroll Visibility
    const handleScroll = () => {
        if (!messageContainerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = messageContainerRef.current;
        const isBottom = scrollHeight - scrollTop - clientHeight < 100;
        setShowScrollButton(!isBottom);
    };

    // Verificar se estamos dentro de uma função antes de colocar returns
    if (loading) {
        return (
            <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#9B4DFF] mx-auto mb-4"></div>
                    <p className="text-gray-600">Carregando Conecta...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-500 mb-4">{error}</p>
                    <button 
                        onClick={() => window.location.reload()} 
                        className="bg-[#9B4DFF] text-white px-4 py-2 rounded-lg hover:bg-[#7B2CDB] transition-colors"
                    >
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8F6FC]">
            <style>{quillStyles}</style>

            {/* Notification messages */}
            {successMessage && (
                <div className="fixed top-4 right-4 z-50">
                    <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center animate-fade-in">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        {successMessage}
                    </div>
                </div>
            )}

            {errorMessage && (
                <div className="fixed top-4 right-4 z-50">
                    <div className="bg-red-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center animate-fade-in">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {errorMessage}
                    </div>
                </div>
            )}

            <div className="container mx-auto p-4 sm:p-6 md:p-8">
                {/* Cabeçalho - não mostrar quando estiver visualizando mensagens */}
                {!viewingMessagesGroupId && !subTab && (
                <header className="flex items-center justify-between mb-8">
                    <div className="flex items-center">
                        {/* Removido o botão de voltar para que o usuário entre diretamente no chat */}
                    </div>
                </header>
                )}

                {/* Automatically redirect to the group chat */}
                {activeTab === 'grupos' && !subTab && !viewingMessagesGroupId && (
                    <div className="flex flex-col items-center mb-8">
                        <h2 className="text-3xl font-bold text-gray-800 mb-10 text-center">Conecta</h2>
                        <p className="text-gray-600 mb-8 text-center max-w-2xl">
                            Conecte-se com outros estudantes, compartilhe conhecimento e fortaleça sua preparação para o vestibular
                        </p>

                        {/* Automatically load the group chat */}
                        <div className="w-full max-w-2xl">
                            <div className="text-center">
                                <p className="text-lg text-gray-600 mb-4">Carregando o chat do grupo...</p>
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto"></div>
                            </div>
                        </div>
                    </div>
                )}


                                {activeTab === 'grupos' && viewingMessagesGroupId && (
                    <div className="h-[100dvh] flex flex-col bg-white z-50">
                        {/* Cabeçalho do grupo */}
                        <div className="bg-violet-600 p-4 flex items-center">
                            <button className="flex items-center text-white font-semibold hover:text-gray-200 transition-colors mr-6" onClick={() => onVoltar && onVoltar()}>
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                                </svg>
                                Voltar
                            </button>
                            <div
                                className="flex items-center cursor-pointer"
                                onClick={() => {
                                    const groupInfo = document.getElementById('groupInfo');
                                    if (groupInfo) {
                                        groupInfo.classList.toggle('hidden');
                                    }
                                }}
                            >
                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mr-3">
                                    <span className="font-semibold text-violet-600 text-lg">
                                        {grupos.find(g => g._id === viewingMessagesGroupId)?.nome?.charAt(0) || 'G'}
                                    </span>
                                </div>
                                <div>
                                    <h3 className="font-bold text-white text-lg">
                                        {grupos.find(g => g._id === viewingMessagesGroupId)?.nome}
                                    </h3>
                                    <p className="text-violet-200 text-sm">
                                        {grupos.find(g => g._id === viewingMessagesGroupId)?.membros?.length || 0} membros
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Área de mensagens */}
                        <div className="flex-1 flex flex-col overflow-hidden">
                            {/* Descrição do grupo (inicialmente oculta) */}
                            <div className="hidden bg-violet-50 border-b border-violet-200 p-4" id="groupInfo">
                                <div className="w-full mx-auto">
                                    <p className="text-gray-700 mb-4">
                                        {grupos.find(g => g._id === viewingMessagesGroupId)?.descricao}
                                    </p>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-600">
                                            {grupos.find(g => g._id === viewingMessagesGroupId)?.membros?.length || 0} membros
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Lista de mensagens - ÚNICO CONTAINER COM SCROLL */}
                            <div className="flex-1 overflow-y-auto p-4 bg-gray-50 mensagens-container">
                                <div className="w-full mx-auto space-y-3 mensagens-container">
                                    {/* Botão para atualizar conversa - fixado no topo */}
                                    <div className="sticky top-0 z-10 bg-transparent pt-4 pb-2">
                                        <div className="flex justify-end">
                                            <button
                                                onClick={() => handleViewMessages(viewingMessagesGroupId)}
                                                className="flex items-center px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 transition-colors"
                                            >
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                                                </svg>
                                                Atualizar conversa
                                            </button>
                                        </div>
                                    </div>
                                    {/* Removendo o container com scroll duplicado */}
                                    {grupos.find(g => g._id === viewingMessagesGroupId)?.messages &&
                                     grupos.find(g => g._id === viewingMessagesGroupId).messages.length > 0 ? (
                                        [...grupos.find(g => g._id === viewingMessagesGroupId).messages].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).map((msg, index, array) => {
                                            // Todas as mensagens alinhadas à esquerda
                                            return (
                                                <div key={msg._id} className="flex items-start">
                                                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-3 flex-shrink-0">
                                                        <span className="text-xs font-semibold text-gray-700">
                                                            {msg.autor?._id === currentUser?._id ? 'V' : (msg.autor?.nome || 'U')?.charAt(0)}
                                                        </span>
                                                    </div>
                                                    <div className="bg-white rounded-lg p-3 max-w-xs md:max-w-md">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <span className="font-semibold text-sm text-gray-800">
                                                                {msg.autor?._id === currentUser?._id ? 'Você' : (msg.autor?.nome || 'Usuário')}
                                                            </span>
                                                            <span className="text-xs text-gray-500 ml-2">
                                                                {msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString('pt-BR', {hour: '2-digit', minute:'2-digit'}) : 'Data inválida'}
                                                            </span>
                                                        </div>
                                                        <p className="text-sm text-gray-700 message-text" dangerouslySetInnerHTML={{ __html: msg.texto || msg.text || '' }}></p>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <p className="text-gray-500 text-center py-4">Nenhuma mensagem ainda</p>
                                    )}
                                </div>
                            </div>

                            {/* Formulário para nova mensagem - Novo design moderno */}
                            <div className="p-4 bg-white border-t border-gray-200 shrink-0">
                                <div className="w-full mx-auto w-full font-sans">
                                    {/* Container Frame */}
                                    <div className={`
                                        relative flex flex-col w-full bg-white
                                        border rounded-xl shadow-sm transition-all duration-300 ease-out
                                        ${messageContent ? 'border-violet-500 ring-4 ring-violet-500/10 shadow-md' : 'border-gray-300 hover:border-gray-400'}
                                    `}>
                                        {/* Editor Area */}
                                        <div className="relative p-1 min-h-[80px]">
                                            <div
                                                ref={messageQuillRef}
                                                id="message-editor-container"
                                                contentEditable
                                                onInput={() => {
                                                    if (messageQuillRef.current) {
                                                        setMessageContent(messageQuillRef.current.innerHTML);
                                                    }
                                                }}
                                                onFocus={() => {
                                                    if (messageQuillRef.current) {
                                                        messageQuillRef.current.setAttribute('contenteditable', 'true');
                                                        messageQuillRef.current.focus();
                                                    }
                                                }}
                                                onBlur={() => {
                                                    // Enviar mensagem quando o usuário sair do campo de texto, se houver conteúdo
                                                    const textContent = messageContent ? messageContent.replace(/<[^>]*>/g, '').trim() : '';
                                                    if (textContent && viewingMessagesGroupId && !isSending) {
                                                        handleSendMessageRef.current && handleSendMessageRef.current(viewingMessagesGroupId, textContent);
                                                        // Limpar o conteúdo após enviar
                                                        if (messageQuillRef.current) {
                                                            messageQuillRef.current.innerHTML = '';
                                                            setMessageContent(''); // Limpar o estado também
                                                        }
                                                    }
                                                }}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault(); // Prevenir nova linha
                                                        // Enviar mensagem quando Enter é pressionado (sem Shift)
                                                        const textContent = messageContent ? messageContent.replace(/<[^>]*>/g, '').trim() : '';
                                                        if (textContent && viewingMessagesGroupId && !isSending) {
                                                            handleSendMessageRef.current && handleSendMessageRef.current(viewingMessagesGroupId, textContent);
                                                            // Limpar o conteúdo após enviar
                                                            if (messageQuillRef.current) {
                                                                messageQuillRef.current.innerHTML = '';
                                                                setMessageContent(''); // Limpar o estado também
                                                            }
                                                        }
                                                    }
                                                }}
                                                data-placeholder="Escreva sua mensagem..."
                                                className="
                                                    w-full h-full min-h-[80px] max-h-[200px] p-4
                                                    outline-none text-gray-800 text-base leading-relaxed
                                                    overflow-y-auto mensagens-container
                                                "
                                                style={{ minHeight: '50px', maxHeight: '150px' }}
                                                role="textbox"
                                                aria-multiline="true"
                                            />
                                        </div>

                                        {/* Footer Actions: Counter & Send Button */}
                                        <div className="flex items-center justify-between px-4 py-3 bg-gray-50/50 border-t border-gray-100 rounded-b-xl backdrop-blur-sm">
                                            {/* Character Counter */}
                                            <span className={`text-xs font-medium transition-colors duration-200 ${
                                                messageContent && messageContent.replace(/<[^>]*>/g, '').length > 180
                                                    ? 'text-red-500'
                                                    : 'text-gray-400'
                                            }`}>
                                                {messageContent ? messageContent.replace(/<[^>]*>/g, '').length : 0}/200 caracteres
                                            </span>

                                            {/* Send Button */}
                                            <button
                                                onClick={() => {
                                                    // Extrair o texto puro do conteúdo HTML para validação
                                                    const textContent = messageContent ? messageContent.replace(/<[^>]*>/g, '').trim() : '';
                                                    if (textContent && viewingMessagesGroupId) {
                                                        handleSendMessageRef.current && handleSendMessageRef.current(viewingMessagesGroupId, textContent);
                                                        // Limpar o conteúdo após enviar
                                                        if (messageQuillRef.current) {
                                                            messageQuillRef.current.innerHTML = '';
                                                            setMessageContent(''); // Limpar o estado também
                                                        }
                                                    }
                                                }}
                                                disabled={isSending || (messageContent && messageContent.replace(/<[^>]*>/g, '').trim().length === 0) || (messageContent && messageContent.replace(/<[^>]*>/g, '').length > 200)}
                                                className={`
                                                    flex items-center justify-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold transition-all duration-200
                                                    ${(messageContent && (messageContent.replace(/<[^>]*>/g, '').trim().length === 0 || messageContent.replace(/<[^>]*>/g, '').length > 200)) || isSending
                                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                        : 'bg-violet-600 text-white hover:bg-violet-700 hover:shadow-lg active:transform active:scale-95'
                                                    }
                                                `}
                                            >
                                                {isSending ? (
                                                    <div className="flex items-center">
                                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Enviando...
                                                    </div>
                                                ) : (
                                                    <span>Enviar</span>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Error Message if over limit */}
                                    {messageError && (
                                        <p className="text-red-500 text-xs mt-2 text-right animate-pulse">
                                            {messageError}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}


                {/* AdSense ad after the main content */}

                {/* Loading overlay when joining, leaving, or accessing a group */}
                {(joiningGroup || leavingGroup || accessingGroup) && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white p-6 rounded-xl shadow-lg flex flex-col items-center">
                            <svg className="animate-spin h-10 w-10 text-violet-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <p className="text-lg font-medium text-gray-800">
                                {joiningGroup ? 'Entrando no grupo...' :
                                 leavingGroup ? 'Saindo do grupo...' :
                                 'Acessando grupo...'}
                            </p>
                            <p className="text-gray-600 mt-2">Aguarde um momento</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};


export default ConectaPage;